import * as React from 'react'
import { cn } from '../../lib/utils'
import { Separator } from './separator'

// ─── AuthLayout ───────────────────────────────────────────────────────────────

export interface AuthLayoutProps extends React.HTMLAttributes<HTMLDivElement> {}

export const AuthLayout = React.forwardRef<HTMLDivElement, AuthLayoutProps>(
	({ className, children, ...props }, ref) => (
		<div
			ref={ref}
			className={cn('flex h-screen w-full overflow-hidden', className)}
			{...props}
		>
			{children}
		</div>
	),
)
AuthLayout.displayName = 'AuthLayout'

// ─── AuthLeftBlock ────────────────────────────────────────────────────────────

export interface AuthLeftBlockProps extends React.HTMLAttributes<HTMLDivElement> {
	/** Slot for the image/illustration in the center of the block */
	image?: React.ReactNode
	/** Wordmark shown at top-left; defaults to "RentFlow" */
	wordmark?: React.ReactNode
}

export const AuthLeftBlock = React.forwardRef<HTMLDivElement, AuthLeftBlockProps>(
	({ className, image, wordmark = 'RentFlow', ...props }, ref) => (
		<div
			ref={ref}
			className={cn('relative hidden lg:flex lg:w-1/2 flex-col bg-(--color-secondary) overflow-hidden', className)}
			{...props}
		>
			{/* Wordmark */}
			<div className="flex items-center h-[60px] px-8 py-2">
				<span className="text-3xl font-semibold leading-9 text-(--color-muted-foreground) tracking-[-0.75px] whitespace-nowrap">
					{wordmark}
				</span>
			</div>

			{/* Illustration */}
			<div className="flex-1 flex items-center justify-center px-8">
				<div className="w-[85%] max-w-[616px] h-[435px] bg-(--color-brand-light) overflow-hidden flex items-center justify-center">
					{image}
				</div>
			</div>
		</div>
	),
)
AuthLeftBlock.displayName = 'AuthLeftBlock'

// ─── AuthRightBlock ───────────────────────────────────────────────────────────

export interface AuthRightBlockProps extends React.HTMLAttributes<HTMLDivElement> {
	/** Optional legal / footer text shown at the bottom */
	footer?: React.ReactNode
}

export const AuthRightBlock = React.forwardRef<HTMLDivElement, AuthRightBlockProps>(
	({ className, children, footer, ...props }, ref) => (
		<div
			ref={ref}
			className={cn(
				'relative w-full lg:w-1/2 bg-(--color-background) flex flex-col',
				className,
			)}
			{...props}
		>
			{/* Content — vertically centered */}
			<div className="flex-1 flex items-center justify-center px-8 lg:px-[148px]">
				<div className="w-full max-w-[424px] flex flex-col">
					{children}
				</div>
			</div>

			{/* Footer */}
			{footer && (
				<div className="flex items-center justify-center px-5 pb-6">
					{footer}
				</div>
			)}
		</div>
	),
)
AuthRightBlock.displayName = 'AuthRightBlock'

// ─── AuthHeading ──────────────────────────────────────────────────────────────

export interface AuthHeadingProps extends React.HTMLAttributes<HTMLHeadingElement> {}

export const AuthHeading = React.forwardRef<HTMLHeadingElement, AuthHeadingProps>(
	({ className, ...props }, ref) => (
		<h1
			ref={ref}
			className={cn(
				'text-2xl font-medium leading-8 text-(--color-foreground) text-center tracking-[-0.6px] w-full',
				className,
			)}
			{...props}
		/>
	),
)
AuthHeading.displayName = 'AuthHeading'

// ─── AuthCaption ──────────────────────────────────────────────────────────────

export interface AuthCaptionProps extends React.HTMLAttributes<HTMLParagraphElement> {}

export const AuthCaption = React.forwardRef<HTMLParagraphElement, AuthCaptionProps>(
	({ className, ...props }, ref) => (
		<p
			ref={ref}
			className={cn(
				'text-sm font-normal leading-5 text-(--color-muted-foreground) text-center',
				className,
			)}
			{...props}
		/>
	),
)
AuthCaption.displayName = 'AuthCaption'

// ─── AuthDivider ──────────────────────────────────────────────────────────────

export interface AuthDividerProps {
	label?: string
}

export function AuthDivider({ label = 'OR' }: AuthDividerProps) {
	return (
		<div className="flex items-center">
			<Separator className="flex-1" />
			<span className="px-2 text-xs font-normal leading-4 text-(--color-muted-foreground) text-center w-[77px]">
				{label}
			</span>
			<Separator className="flex-1" />
		</div>
	)
}
